import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

export function SocketProvider({ children }) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const socketRef  = useRef(null);
  const [socket, setSocket]             = useState(null);
  const [isConnected, setIsConnected]   = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  useEffect(() => {
    if (user && token) {
      const serverUrl = process.env.REACT_APP_API_URL || 'http://localhost:5003';
      socketRef.current = io(serverUrl, {
        auth:                  { token },
        transports:            ['polling', 'websocket'], // polling d'abord = compatible réseaux mobiles restrictifs
        upgrade:               true,
        reconnection:          true,
        reconnectionAttempts:  Infinity,
        reconnectionDelay:     1000,
        reconnectionDelayMax:  10000,
        randomizationFactor:   0.3,
        timeout:               20000,
      });

      setSocket(socketRef.current);

      socketRef.current.on('connect', () => {
        setIsConnected(true);
        setReconnecting(false);
        console.log('[Socket] Connecté:', socketRef.current.id);
      });

      socketRef.current.on('disconnect', (reason) => {
        setIsConnected(false);
        console.log('[Socket] Déconnecté :', reason);
        if (reason !== 'io client disconnect') {
          // Déconnexion involontaire — signaler à l'utilisateur
          toast.error('Connexion perdue. Reconnexion...', { id: 'socket-offline', duration: 4000 });
        }
      });

      socketRef.current.on('reconnect_attempt', (n) => {
        setReconnecting(true);
        console.log(`[Socket] Tentative de reconnexion #${n}`);
      });

      socketRef.current.on('reconnect', () => {
        setIsConnected(true);
        setReconnecting(false);
        toast.success('Reconnecté !', { id: 'socket-online', duration: 2000 });
      });

      socketRef.current.on('connect_error', (err) => {
        console.error('[Socket] Erreur connexion:', err.message);
      });

      // Défi reçu d'un ami — toast avec bouton "Rejoindre"
      socketRef.current.on('duel-challenge', ({ gameId, code, challengerPseudo }) => {
        toast(
          (t) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontWeight: 700, color: '#FFB81C' }}>
                ⚔️ {challengerPseudo} te défie !
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => {
                    toast.dismiss(t.id);
                    navigate(`/duel?gameId=${gameId}`);
                  }}
                  style={{
                    background: '#FFB81C', color: '#001a4d',
                    border: 'none', borderRadius: 8, padding: '6px 14px',
                    fontWeight: 700, cursor: 'pointer', fontSize: 12,
                  }}
                >
                  REJOINDRE
                </button>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  style={{
                    background: 'rgba(255,255,255,0.1)', color: '#fff',
                    border: 'none', borderRadius: 8, padding: '6px 12px',
                    cursor: 'pointer', fontSize: 12,
                  }}
                >
                  Ignorer
                </button>
              </div>
            </div>
          ),
          { duration: 15000, id: `challenge-${gameId}` }
        );
      });

    } else {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
        setReconnecting(false);
      }
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
      }
    };
  }, [user, token]);

  const value = {
    socket,
    isConnected,
    reconnecting,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

// Hook d'accès au socket
export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket doit être utilisé dans SocketProvider');
  }
  return context;
}

export default SocketContext;

